'use client'

import * as React from 'react'
import useEmblaCarousel, { type UseEmblaCarouselType } from 'embla-carousel-react'

import { cn } from '../../lib/cn'
import { ChevronLeft, ChevronRight } from '../../icons/chevrons.tsx'

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

interface CarouselProps {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  orientation?: 'horizontal' | 'vertical'
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel (): CarouselContextProps {
  const context = React.useContext(CarouselContext)

  if (context == null) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

function Carousel ({
  orientation = 'horizontal',
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & CarouselProps): JSX.Element {
  const [carouselRef, api] = useEmblaCarousel(
    {
      ...opts,
      axis: orientation === 'horizontal' ? 'x' : 'y'
    },
    plugins
  )
  const [canScrollPrev, setCanScrollPrev] = React.useState(false)
  const [canScrollNext, setCanScrollNext] = React.useState(false)

  const onSelect = React.useCallback((api: CarouselApi) => {
    if (api == null) return
    setCanScrollPrev(api.canScrollPrev())
    setCanScrollNext(api.canScrollNext())
  }, [])

  const scrollPrev = React.useCallback(() => {
    api?.scrollPrev()
  }, [api])

  const scrollNext = React.useCallback(() => {
    api?.scrollNext()
  }, [api])

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        scrollPrev()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        scrollNext()
      }
    },
    [scrollPrev, scrollNext]
  )

  React.useEffect(() => {
    if ((api == null) || (setApi == null)) return
    setApi(api)
  }, [api, setApi])

  React.useEffect(() => {
    if (api == null) return
    onSelect(api)
    api.on('reInit', onSelect)
    api.on('select', onSelect)

    return () => {
      api?.off('select', onSelect)
    }
  }, [api, onSelect])

  return (
    <CarouselContext.Provider
      value={{
        carouselRef,
        api,
        opts,
        orientation:
          orientation ?? (opts?.axis === 'y' ? 'vertical' : 'horizontal'),
        scrollPrev,
        scrollNext,
        canScrollPrev,
        canScrollNext
      }}
    >
      <div
        onKeyDownCapture={handleKeyDown}
        className={cn('relative', className)}
        role='region'
        aria-roledescription='carousel'
        data-slot='carousel'
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  )
}

function CarouselContent ({ className, ...props }: React.ComponentProps<'div'>): JSX.Element {
  const { carouselRef, orientation } = useCarousel()

  return (
    <div
      ref={carouselRef}
      className='overflow-hidden'
      data-slot='carousel-content'
    >
      <div
        className={cn(
          'flex',
          orientation === 'horizontal' ? '-ml-4' : '-mt-4 flex-col',
          className
        )}
        {...props}
      />
    </div>
  )
}

function CarouselItem ({ className, ...props }: React.ComponentProps<'div'>): JSX.Element {
  const { orientation } = useCarousel()

  return (
    <div
      role='group'
      aria-roledescription='slide'
      data-slot='carousel-item'
      className={cn(
        'min-w-0 shrink-0 grow-0 basis-full',
        orientation === 'horizontal' ? 'pl-4' : 'pt-4',
        className
      )}
      {...props}
    />
  )
}

function CarouselPrevious ({
  className,
  ...props
}: React.ComponentProps<'button'>): JSX.Element {
  const { orientation, scrollPrev, canScrollPrev } = useCarousel()

  return (
    <button
      data-slot='carousel-previous'
      className={cn(
        'rounded-full absolute touch-manipulation size-6 @desktop:size-12 bg-white flex justify-center items-center text-brand-primary disabled:text-neutral-600 disabled:cursor-not-allowed disabled:opacity-70',
        orientation === 'horizontal'
          ? 'top-1/2 left-6 @desktop:left-12 -translate-y-1/2'
          : 'top-6 @desktop:top-12 left-1/2 -translate-x-1/2 rotate-90',
        className
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <ChevronLeft className='cn-rtl-flip size-3 @desktop:size-5' />
      <span className='sr-only'>Previous slide</span>
    </button>
  )
}

function CarouselNext ({
  className,
  ...props
}: React.ComponentProps<'button'>): JSX.Element {
  const { orientation, scrollNext, canScrollNext } = useCarousel()

  return (
    <button
      data-slot='carousel-next'
      className={cn(
        'rounded-full absolute touch-manipulation size-6 @desktop:size-12 bg-white flex justify-center items-center text-brand-primary disabled:text-neutral-600 disabled:cursor-not-allowed disabled:opacity-70',
        orientation === 'horizontal'
          ? 'top-1/2 right-6 @desktop:right-12 -translate-y-1/2'
          : 'bottom-6 @desktop:bottom-12 left-1/2 -translate-x-1/2 rotate-90',
        className
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <ChevronRight className='cn-rtl-flip size-3 @desktop:size-5' />
      <span className='sr-only'>Next slide</span>
    </button>
  )
}

export {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  useCarousel
}
